import React from 'react'

interface MarkdownRendererProps {
  content: string
  className?: string
}

export default function MarkdownRenderer({ content, className }: MarkdownRendererProps) {
  // Simple markdown renderer - converts basic markdown to HTML
  
  const convertMarkdown = (text: string): string => {
    let html = text

    // Headers
    html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>')
    html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>')
    html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>')

    // Bold
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')

    // Italic  
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>')

    // Code blocks
    html = html.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')

    // Inline code
    html = html.replace(/`([^`]*)`/g, '<code>$1</code>')

    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-purple-600 hover:text-purple-800 underline">$1</a>')

    // Lists
    html = html.replace(/^\* (.*$)/gim, '<li>$1</li>')
    html = html.replace(/^\d+\. (.*$)/gim, '<li>$1</li>')

    // Wrap consecutive list items in ul tags
    html = html.replace(/(<li>.*<\/li>\s*)+/g, (match) => `<ul class="list-disc list-inside space-y-2 mb-4">${match}</ul>`)

    // Blockquotes
    html = html.replace(/^> (.*$)/gim, '<blockquote class="border-l-4 border-purple-300 pl-4 italic text-gray-600 my-4">$1</blockquote>')

    // Tables - basic table support
    const tableRegex = /\|(.+)\|\n\|[\s\-\|]+\|\n((?:\|.+\|\n?)*)/g
    html = html.replace(tableRegex, (match, header, rows) => {
      const headerCells = header.split('|').map((cell: string) => cell.trim()).filter(Boolean)
      const headerRow = '<tr>' + headerCells.map((cell: string) => `<th class="border border-gray-300 px-4 py-2 bg-gray-50">${cell}</th>`).join('') + '</tr>'
      
      const bodyRows = rows.split('\n').filter(Boolean).map((row: string) => {
        const cells = row.split('|').map((cell: string) => cell.trim()).filter(Boolean)
        return '<tr>' + cells.map((cell: string) => `<td class="border border-gray-300 px-4 py-2">${cell}</td>`).join('') + '</tr>'
      }).join('')
      
      return `<table class="w-full border-collapse border border-gray-300 my-4">${headerRow}${bodyRows}</table>`
    })

    // Paragraphs (double newlines)
    html = html.replace(/\n\s*\n/g, '</p><p>')
    html = '<p>' + html + '</p>'

    // Clean up empty paragraphs
    html = html.replace(/<p><\/p>/g, '')
    html = html.replace(/<p>\s*<\/p>/g, '')
    
    // Add paragraph styling
    html = html.replace(/<p>/g, '<p class="mb-4">')

    return html
  }

  const htmlContent = convertMarkdown(content)

  return (
    <div 
      className={`prose prose-purple dark:prose-invert max-w-none ${className || ''}`}
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  )
}